import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenantLocation, useMoney } from '../context/LocationContext';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { RefreshCw, Loader2, Calculator, FileText, Banknote, BookOpen, Trash2 } from 'lucide-react';
import { cn } from '../lib/utils';

export default function Accounting() {
  const { token } = useAuth();
  const { currentLocationId } = useTenantLocation();
  const { money, totals } = useMoney();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await api.getJournalEntries(token, params);
      setEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch journal entries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, statusFilter, currentLocationId]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const result = await api.generateAccountingEntries(token);
      toast.success(result.data.message);
      fetchEntries();
    } catch (error) {
      toast.error('Failed to generate entries');
    } finally {
      setGenerating(false);
    }
  };

  const handleClearDemo = async () => {
    if (!window.confirm(
      'Remove all seeded demo data for this tenant? Real POS data (UnTill / CSV / webhook / manual) will be kept.'
    )) return;
    setClearing(true);
    try {
      const r = await api.clearDemoData(token);
      const t = r.data.total || 0;
      toast.success(t > 0 ? `Cleared ${t} demo rows` : 'No demo rows found to clear');
      fetchEntries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear demo data');
    } finally {
      setClearing(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      posted: 'status-success',
      draft: 'status-synced',
      pending: 'status-pending'
    };
    return styles[status] || 'status-pending';
  };

  const totalAmount = totals(entries, (e) => e.amount);
  const postedCount = entries.filter(e => e.status === 'posted').length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="accounting-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-white tracking-tight">
            Accounting
          </h1>
          <p className="text-slate-400 mt-1">Auto-generated journal entries from synced sales</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleClearDemo}
            disabled={clearing}
            variant="outline"
            className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 hover:text-white"
            data-testid="clear-demo-btn"
          >
            {clearing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Clear demo data
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={generating}
            className="bg-blue-500 hover:bg-blue-600 text-white"
            data-testid="generate-entries-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calculator className="w-4 h-4 mr-2" />}
            Generate Entries
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Entries</p>
                <p className="text-2xl font-bold text-white mt-1">{entries.length}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Amount</p>
                <p className="text-2xl font-bold text-emerald-400 mt-1" data-testid="accounting-total-amount">{totalAmount}</p>
              </div>
              <Banknote className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Posted</p>
                <p className="text-2xl font-bold text-blue-400 mt-1">{postedCount}</p>
              </div>
              <BookOpen className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Pending</p>
                <p className="text-2xl font-bold text-amber-400 mt-1">
                  {entries.filter(e => e.status === 'pending' || e.status === 'draft').length}
                </p>
              </div>
              <Calculator className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm">Filter by status:</span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] bg-slate-800/50 border-slate-700 text-white" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="posted">Posted</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Entries Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white">Journal Entries</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No journal entries found. Generate entries from synced sales.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-slate-400">Entry #</TableHead>
                  <TableHead className="text-slate-400">Date</TableHead>
                  <TableHead className="text-slate-400">Description</TableHead>
                  <TableHead className="text-slate-400">Debit Account</TableHead>
                  <TableHead className="text-slate-400">Credit Account</TableHead>
                  <TableHead className="text-slate-400">Amount</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id} className="border-slate-800 data-table-row">
                    <TableCell className="font-mono text-white">{entry.entry_number}</TableCell>
                    <TableCell className="text-slate-300">{entry.date}</TableCell>
                    <TableCell className="text-slate-300 max-w-[200px] truncate">
                      {entry.description}
                    </TableCell>
                    <TableCell className="text-blue-400 text-sm">{entry.debit_account}</TableCell>
                    <TableCell className="text-emerald-400 text-sm">{entry.credit_account}</TableCell>
                    <TableCell className="text-white font-mono font-medium">
                      {money(entry.amount, entry.location_id)}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusBadge(entry.status)}>{entry.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
